
import { useState } from "react";
import { EventObject, CalendarViewMode } from "@/components/calendar/types";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/providers/AuthProvider";
import { useQueryClient } from "@tanstack/react-query";

export const useCalendarState = () => {
	const [currentDate, setCurrentDate] = useState<Date>(new Date());
	const [viewMode, setViewMode] = useState<CalendarViewMode>("week");
	const [selectedDate, setSelectedDate] = useState<Date | null>(null);
	const [showCreateModal, setShowCreateModal] = useState(false);
	const [selectedSlotDate, setSelectedSlotDate] = useState<Date | undefined>(undefined);
	
	const { user } = useAuth();
	const queryClient = useQueryClient();

	const navigateCalendar = (direction: "prev" | "next") => {
		const newDate = new Date(currentDate);
		if (viewMode === "day") {
			direction === "prev" ? newDate.setDate(newDate.getDate() - 1) : newDate.setDate(newDate.getDate() + 1);
		} else if (viewMode === "week") {
			direction === "prev" ? newDate.setDate(newDate.getDate() - 7) : newDate.setDate(newDate.getDate() + 7);
		} else {
			direction === "prev" ? newDate.setMonth(newDate.getMonth() - 1) : newDate.setMonth(newDate.getMonth() + 1);
		}
		setCurrentDate(newDate);
	};

	const goToToday = () => setCurrentDate(new Date());

	const handleEventClick = (event: EventObject) => {
		console.log("Event clicked:", event);
	};

	const handleDateClick = (date: Date) => {
		setSelectedDate(date);
		if (viewMode === "month") {
			setViewMode("day");
			setCurrentDate(date);
		}
	};

	const handleEventDrop = async (eventId: string, newStart: Date, newEnd: Date) => {
		if (!user) {
			toast.error("You must be logged in to update events");
			return;
		}

		try {
			const durationMs = newEnd.getTime() - newStart.getTime();
			const durationMinutes = Math.floor(durationMs / (1000 * 60));

			const { error } = await supabase
				.from('actions')
				.update({
					d_begin: newStart.toISOString(),
					d_end: newEnd.toISOString(),
					d_duration: durationMinutes,
					updated_at: new Date().toISOString()
				})
				.eq('id', eventId);

			if (error) throw error;
			
			// Invalidate queries to refresh the calendar data
			await queryClient.invalidateQueries({ queryKey: ["actions", "Calendar"] });
			toast.success("Event updated successfully");
		} catch (err) {
			console.error("Error updating event:", err);
			toast.error("Failed to update event");
			throw err;
		}
	};

	const handleEventCreate = async (eventData: { 
		title: string; 
		start: Date; 
		end: Date; 
		description?: string;
		color?: string;
		relations?: {
			candidates: string[];
			offers: string[];
			companies: string[];
			matches: string[];
			contacts: string[];
		}
	}) => {
		try {
			const durationMs = eventData.end.getTime() - eventData.start.getTime();
			const durationMinutes = Math.floor(durationMs / (1000 * 60));
			
			// Create metadata object with relations and color
			const metadata: Record<string, any> = {};
			
			// Add color if it exists
			if (eventData.color) {
				metadata.color = eventData.color;
			}
			
			// Add relations to metadata if they exist
			if (eventData.relations) {
				if (eventData.relations.candidates?.length > 0) 
					metadata.candidates = eventData.relations.candidates;
				
				if (eventData.relations.offers?.length > 0)
					metadata.offers = eventData.relations.offers;
				
				if (eventData.relations.companies?.length > 0)
					metadata.companies = eventData.relations.companies;
				
				if (eventData.relations.matches?.length > 0)
					metadata.matches = eventData.relations.matches;
				
				if (eventData.relations.contacts?.length > 0)
					metadata.contacts = eventData.relations.contacts;
			}
			
			console.log("Creating event with metadata:", metadata);

			const { data, error } = await supabase
				.from('actions')
				.insert([{
					name: eventData.title,
					description: eventData.description || null,
					action_type: 'Calendar',
					d_begin: eventData.start.toISOString(),
					d_end: eventData.end.toISOString(),
					d_duration: durationMinutes,
					action_status: 'scheduled',
					user_id: user.id,
					metadata // Store all additional data in metadata
				}]);

			if (error) throw error;
			
			// Invalidate queries to refresh the calendar data
			await queryClient.invalidateQueries({ queryKey: ["actions", "Calendar"] });
		} catch (err) {
			console.error("Error in event creation:", err);
			throw err;
		}
	};

	const handleEventUpdate = async (eventId: string, eventData: { 
		title: string; 
		start: Date; 
		end: Date; 
		description?: string;
		color?: string;
		relations?: {
			candidates: string[];
			offers: string[];
			companies: string[];
			matches: string[];
			contacts: string[];
		}
	}) => {
		if (!user) {
			toast.error("You must be logged in to update events");
			return;
		}

		try {
			const durationMs = eventData.end.getTime() - eventData.start.getTime();
			const durationMinutes = Math.floor(durationMs / (1000 * 60));
			
			// Create a new metadata object
			const metadata: Record<string, any> = {};
			
			// Add color to metadata if it exists
			if (eventData.color) {
				metadata.color = eventData.color;
			}
			
			// Add relations to metadata if they exist
			if (eventData.relations) {
				metadata.candidates = eventData.relations.candidates || [];
				metadata.offers = eventData.relations.offers || [];
				metadata.companies = eventData.relations.companies || [];
				metadata.matches = eventData.relations.matches || [];
				metadata.contacts = eventData.relations.contacts || [];
			}
			
			console.log("Updating event with metadata:", metadata);

			const { error } = await supabase
				.from('actions')
				.update({
					name: eventData.title,
					description: eventData.description || null,
					d_begin: eventData.start.toISOString(),
					d_end: eventData.end.toISOString(),
					d_duration: durationMinutes,
					updated_at: new Date().toISOString(),
					metadata
				})
				.eq('id', eventId);

			if (error) throw error;
			
			// Invalidate queries to refresh the calendar data
			await queryClient.invalidateQueries({ queryKey: ["actions", "Calendar"] });
		} catch (err) {
			console.error("Error in event update:", err);
			throw err;
		}
	};

	const handleEventDelete = async (eventId: string) => {
		if (!user) {
			toast.error("You must be logged in to delete events");
			return;
		}

		try {
			const { error } = await supabase
				.from('actions')
				.delete()
				.eq('id', eventId);

			if (error) throw error;
			
			// Invalidate queries to refresh the calendar data
			await queryClient.invalidateQueries({ queryKey: ["actions", "Calendar"] });
		} catch (err) {
			console.error("Error in event deletion:", err);
			throw err;
		}
	};

	const handleTimeSlotClick = (hour: number, day: Date) => {
		const slotDate = new Date(day);
		const hourPart = Math.floor(hour);
		const minutePart = Math.round((hour % 1) * 60);
		
		slotDate.setHours(hourPart);
		slotDate.setMinutes(minutePart);
		slotDate.setSeconds(0);
		
		setSelectedSlotDate(slotDate);
		setShowCreateModal(true);
	};

	return {
		currentDate,
		viewMode,
		selectedDate,
		showCreateModal,
		selectedSlotDate,
		setShowCreateModal,
		setViewMode,
		navigateCalendar,
		goToToday,
		handleEventClick,
		handleDateClick,
		handleEventDrop,
		handleEventCreate,
		handleEventUpdate,
		handleEventDelete,
		handleTimeSlotClick
	};
};
